import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  AlertTriangle,
  Check,
  Copy,
  Download,
  Eye,
  FileText,
  Lightbulb,
  Lock,
  Package,
  Pencil,
  Printer,
  RotateCcw,
  ShieldCheck,
} from "lucide-react";
import jsPDF from "jspdf";
import JSZip from "jszip";
import { toast, Toaster } from "sonner";

import Header from "../components/Header";
import Footer from "../components/Footer";
import { buildLetter } from "../lib/letterTemplates";
import { getCase } from "../lib/letterCases";
import { getResult } from "../lib/resultStore";
import { trackEvent } from "@/lib/analytics";

const LRAR_URL = "https://www.merci-facteur.com#p_ref=173";

const LEBARA_URL = "https://aklam.io/iDpz0fSl";
const LEBARA_CASES = ["banque"];

const TOPCASHBACK_URL = "https://www.topcashback.fr/ref/member101337706034";
const TOPCASHBACK_CASES = ["remboursement", "non-livre", "consommation"];

const INCOGNI_URL = "https://deal.incogni.io/aff_c?offer_id=2&aff_id=2599";
const INCOGNI_CASES = ["rgpd"];

function ResultInfoCards({ requiresLRAR }) {
  return (
    <div className="no-print mt-8 grid gap-4 md:grid-cols-2">
      <div className="rounded-lg border border-border bg-card p-5">
        <div className="flex items-start gap-3">
          {requiresLRAR ? (
            <AlertTriangle
              size={18}
              className="shrink-0 mt-0.5 text-[#e8502a]"
            />
          ) : (
            <Lightbulb size={18} className="shrink-0 mt-0.5 text-amber-500" />
          )}

          <div>
            <p className="text-sm font-semibold">
              {requiresLRAR ? "Attention importante" : "Conseil pratique"}
            </p>
            <p className="mt-1.5 text-sm leading-relaxed text-foreground/65">
              {requiresLRAR
                ? "Pour cette démarche, il peut être important de conserver une preuve d’envoi et de réception. L’envoi en Lettre Recommandée avec Accusé de Réception peut être utile selon votre situation."
                : "Vous pouvez d’abord envoyer ce courrier par email ou lettre simple. Si vos demandes restent sans réponse, un envoi en Lettre Recommandée peut permettre de conserver une preuve plus formelle."}
            </p>
          </div>
        </div>
      </div>

      <div className="rounded-lg border border-border bg-card p-5">
        <div className="flex items-start gap-3">
          <Lock size={18} className="shrink-0 mt-0.5 text-trust" />
          <div>
            <p className="text-sm font-semibold">Vie privée</p>
            <p className="mt-1.5 text-sm leading-relaxed text-foreground/65">
              Votre dossier est préparé dans Clameo, mais il n’est pas envoyé
              automatiquement. Les pièces jointes ajoutées servent uniquement à
              constituer le dossier téléchargeable et sont supprimées
              automatiquement au plus tard 7 jours après leur création.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

function MerciFacteurCard({ caseId }) {
  return (
    <div
      className="rounded-lg p-4 relative overflow-hidden transition-all"
      style={{
        background:
          "linear-gradient(135deg, #fff8f6 0%, #ffffff 48%, #fff1ed 100%)",
        border: "1px solid #f8b4a3",
        boxShadow: "0 14px 36px rgba(232, 80, 42, 0.12)",
      }}
    >
      <div
        className="absolute -right-10 -top-10 h-28 w-28 rounded-full opacity-20"
        style={{ background: "#e8502a" }}
        aria-hidden="true"
      />

      <div className="relative">
        <div className="mb-3 flex items-center justify-between gap-3">
          <div className="flex min-w-0 items-center gap-2">
            <img
              src="/partners/merci_facteur.png"
              alt="Merci Facteur"
              className="h-7 w-auto max-w-[120px] object-contain"
            />
            <p className="truncate text-sm font-black text-foreground">
              Merci Facteur
            </p>
          </div>

          <span
            className="shrink-0 rounded-full border bg-white px-2 py-1 text-[10px] font-black uppercase tracking-wider shadow-sm"
            style={{ color: "#e8502a", borderColor: "#f8b4a3" }}
          >
            Partenaire
          </span>
        </div>

        <p className="mb-2 text-lg font-black tracking-tight">
          <span style={{ color: "#e8502a" }}>
            Envoyer votre courrier en recommandé
          </span>
        </p>

        <p className="mb-3 text-sm leading-relaxed text-foreground/75">
          Vous pouvez imprimer et envoyer votre lettre en{" "}
          <strong>Lettre Recommandée avec Accusé de Réception</strong>{" "}
          directement en ligne via Merci Facteur.
        </p>

        <p className="mb-5 text-xs leading-relaxed text-foreground/55">
          Clameo ne gère pas ce service. Vérifiez toujours les tarifs, délais,
          options d’envoi et conditions du partenaire avant validation.
        </p>

        <a
          href={LRAR_URL}
          target="_blank"
          rel="noreferrer"
          onClick={() =>
            trackEvent("partner_click", {
              partner: "merci_facteur",
              placement: "result_sidebar",
              case_type: caseId,
            })
          }
          className="inline-flex w-full items-center justify-center gap-2 rounded-[14px] px-4 py-3 text-sm font-black text-white shadow-md transition-all transform hover:scale-[1.02]"
          style={{
            background: "linear-gradient(135deg, #111827 0%, #e8502a 100%)",
          }}
        >
          Envoyer mon courrier <ArrowRight size={16} />
        </a>

        <p
          className="mt-4 flex items-center justify-center gap-1.5 text-center text-xs font-medium"
          style={{ color: "#e8502a" }}
        >
          <ShieldCheck size={14} /> Preuve de dépôt et suivi inclus
        </p>
      </div>
    </div>
  );
}

function IncogniCard({ caseId }) {
  return (
    <div
      className="rounded-lg p-4 relative overflow-hidden transition-all"
      style={{
        background:
          "linear-gradient(135deg, #f8fafc 0%, #ffffff 48%, #eef2ff 100%)",
        border: "1px solid #c7d2fe",
        boxShadow: "0 14px 36px rgba(30, 64, 175, 0.10)",
      }}
    >
      <div
        className="absolute -right-10 -top-10 h-28 w-28 rounded-full opacity-15"
        style={{ background: "#1e40af" }}
        aria-hidden="true"
      />

      <div className="relative">
        <div className="mb-3 flex items-center justify-between gap-3">
          <div className="flex min-w-0 items-center gap-2">
            <img
              src="/partners/incogni.png"
              alt="Incogni"
              className="h-7 w-auto max-w-[112px] object-contain"
            />
            <p className="truncate text-sm font-black text-foreground">
              Incogni
            </p>
          </div>

          <span
            className="shrink-0 rounded-full border bg-white px-2 py-1 text-[10px] font-black uppercase tracking-wider shadow-sm"
            style={{ color: "#1e40af", borderColor: "#c7d2fe" }}
          >
            Partenaire
          </span>
        </div>

        <h3 className="mb-2 text-lg font-black tracking-tight">
          <span style={{ color: "#1e40af" }}>
            Suppressions automatisées de données personnelles
          </span>
        </h3>

        <p className="mb-3 text-sm leading-relaxed text-foreground/75">
          Votre courrier RGPD est prêt. Vous pouvez aussi découvrir Incogni, un
          service partenaire conçu pour automatiser certaines demandes de
          suppression de données personnelles, selon les conditions en vigueur.
        </p>

        <p className="mb-5 text-xs leading-relaxed text-foreground/55">
          Clameo ne gère pas ce service. Vérifiez toujours les conditions, la
          couverture et l’abonnement avant de souscrire.
        </p>

        <a
          href={INCOGNI_URL}
          target="_blank"
          rel="noreferrer"
          onClick={(e) => {
            if (INCOGNI_URL === "#") {
              e.preventDefault();
              return;
            }

            trackEvent("partner_click", {
              partner: "incogni",
              placement: "result_sidebar",
              case_type: caseId,
            });
          }}
          className="inline-flex w-full items-center justify-center gap-2 rounded-[14px] px-4 py-3 text-sm font-black text-white shadow-md transition-all transform hover:scale-[1.02]"
          style={{
            background: "linear-gradient(135deg, #0f172a 0%, #1e40af 100%)",
          }}
        >
          Découvrir Incogni <ArrowRight size={16} />
        </a>

        <p className="mt-4 flex items-center justify-center gap-1.5 text-center text-xs font-medium text-foreground/50">
          Service soumis aux conditions du partenaire
        </p>
      </div>
    </div>
  );
}

function LebaraCard({ caseId }) {
  return (
    <div
      className="rounded-lg p-4 relative overflow-hidden transition-all"
      style={{
        background:
          "linear-gradient(135deg, #eef9ff 0%, #ffffff 46%, #fff7fb 100%)",
        border: "1px solid #7dd3fc",
        boxShadow: "0 14px 36px rgba(0, 166, 235, 0.12)",
      }}
    >
      <div
        className="absolute -right-10 -top-10 h-28 w-28 rounded-full opacity-20"
        style={{ background: "#00A6EB" }}
        aria-hidden="true"
      />

      <div className="relative">
        <div className="mb-3 flex items-center justify-between gap-3">
          <div className="flex min-w-0 items-center gap-2">
            <img
              src="/partners/lebara.png"
              alt="Lebara"
              className="h-6 w-auto max-w-[96px] object-contain"
            />
            <p className="truncate text-sm font-black text-foreground">
              Forfaits Lebara
            </p>
          </div>

          <span
            className="shrink-0 rounded-full border bg-white px-2 py-1 text-[10px] font-black uppercase tracking-wider shadow-sm"
            style={{ color: "#0369a1", borderColor: "#bae6fd" }}
          >
            Partenaire
          </span>
        </div>

        <p className="mb-3 text-xl font-black tracking-tight">
          <span style={{ color: "#00A6EB" }}>50% pendant 2 mois</span>
        </p>

        <p className="mb-3 text-sm leading-relaxed text-foreground/75">
          Vous pouvez découvrir l’offre mobile Lebara avec{" "}
          <strong>50% de réduction pendant les 2 premiers mois</strong>, selon
          les conditions en vigueur.
        </p>

        <p className="mb-5 text-xs leading-relaxed text-foreground/55">
          Clameo ne gère pas ce service. Vérifiez les tarifs, la durée de
          l’offre et les conditions avant de souscrire.
        </p>

        <a
          href={LEBARA_URL}
          target="_blank"
          rel="noreferrer"
          onClick={() =>
            trackEvent("partner_click", {
              partner: "lebara",
              placement: "result_sidebar",
              case_type: caseId,
            })
          }
          className="inline-flex w-full items-center justify-center gap-2 rounded-[14px] px-4 py-3 text-sm font-black text-white shadow-md transition-all transform hover:scale-[1.02]"
          style={{
            background: "linear-gradient(135deg, #0284c7 0%, #00A6EB 100%)",
          }}
        >
          Découvrir l’offre Lebara <ArrowRight size={16} />
        </a>

        <p className="mt-4 flex items-center justify-center gap-1.5 text-center text-xs font-medium text-foreground/50">
          Offre soumise aux conditions du partenaire
        </p>
      </div>
    </div>
  );
}

function TopCashbackCard({ caseId }) {
  return (
    <div
      className="rounded-lg p-4 relative overflow-hidden transition-all"
      style={{
        background:
          "linear-gradient(135deg, #ecfdf5 0%, #ffffff 48%, #f0fdf4 100%)",
        border: "1px solid #86efac",
        boxShadow: "0 14px 36px rgba(22, 163, 74, 0.12)",
      }}
    >
      <div
        className="absolute -right-10 -top-10 h-28 w-28 rounded-full opacity-20"
        style={{ background: "#16a34a" }}
        aria-hidden="true"
      />

      <div className="relative">
        <div className="mb-3 flex items-center justify-between gap-3">
          <div className="flex min-w-0 items-center gap-2">
            <img
              src="/partners/topcashback.png"
              alt="TopCashback"
              className="h-6 w-auto max-w-[130px] object-contain"
            />
            <p className="truncate text-sm font-black text-foreground">
              TopCashback
            </p>
          </div>

          <span
            className="shrink-0 rounded-full border bg-white px-2 py-1 text-[10px] font-black uppercase tracking-wider shadow-sm"
            style={{ color: "#15803d", borderColor: "#bbf7d0" }}
          >
            Partenaire
          </span>
        </div>

        <p className="mb-2 text-lg font-black tracking-tight">
          <span style={{ color: "#16a34a" }}>
            Cashback sur vos prochains achats
          </span>
        </p>

        <p className="mb-3 text-sm leading-relaxed text-foreground/75">
          Votre courrier vous aide à traiter ce litige. Pour vos futurs achats
          en ligne, vous pouvez aussi découvrir TopCashback et vérifier les
          offres de cashback disponibles selon les marchands, avec certaines
          offres pouvant aller jusqu’à 50% selon les conditions en vigueur.
        </p>

        <p className="mb-5 text-xs leading-relaxed text-foreground/55">
          Clameo ne gère pas ce service. Vérifiez toujours les conditions de
          chaque marchand, les délais de validation et les exclusions avant
          d’acheter.
        </p>

        <a
          href={TOPCASHBACK_URL}
          target="_blank"
          rel="noreferrer"
          onClick={() =>
            trackEvent("partner_click", {
              partner: "topcashback",
              placement: "result_sidebar",
              case_type: caseId,
            })
          }
          className="inline-flex w-full items-center justify-center gap-2 rounded-[14px] px-4 py-3 text-sm font-black text-white shadow-md transition-all transform hover:scale-[1.02]"
          style={{
            background: "linear-gradient(135deg, #15803d 0%, #16a34a 100%)",
          }}
        >
          Découvrir TopCashback <ArrowRight size={16} />
        </a>

        <p className="mt-4 flex items-center justify-center gap-1.5 text-center text-xs font-medium text-foreground/50">
          Offres soumises aux conditions des marchands
        </p>
      </div>
    </div>
  );
}

export default function Result() {
  const [payload, setPayload] = useState(null);
  const [copied, setCopied] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editedText, setEditedText] = useState(null);
  const [zipping, setZipping] = useState(false);

  useEffect(() => {
    const result = getResult();
    setPayload(result || null);
  }, []);

  const generatedText = useMemo(() => {
    if (!payload) return "";

    try {
      return buildLetter(payload.caseId, payload.data);
    } catch (error) {
      console.error(error);
      return "";
    }
  }, [payload]);

  const baseText = editedText !== null ? editedText : generatedText;
  const isEdited = editedText !== null && editedText !== generatedText;
  const c = payload ? getCase(payload.caseId) : null;

  const uploadedAttachments = payload?.data?.uploadedAttachments || [];
  const hasAttachments = uploadedAttachments.length > 0;

  const requiresLRAR =
    payload && ["mise-en-demeure", "logement", "employeur"].includes(payload.caseId);

  const showIncogniPartner =
    payload?.caseId && INCOGNI_CASES.includes(payload.caseId);
  const showLebaraPartner =
    payload?.caseId && LEBARA_CASES.includes(payload.caseId);
  const showTopCashbackPartner =
    payload?.caseId && TOPCASHBACK_CASES.includes(payload.caseId);

  const buildPDFDoc = () => {
    const doc = new jsPDF({ unit: "pt", format: "a4" });
    const margin = 56;
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const maxW = pageWidth - margin * 2;
    const lineHeight = 15;

    doc.setFont("helvetica", "normal");
    doc.setFontSize(10.5);

    const lines = doc.splitTextToSize(baseText, maxW);
    let y = margin;

    lines.forEach((line) => {
      if (y + lineHeight > pageHeight - margin) {
        doc.addPage();
        y = margin;
      }
      doc.text(line, margin, y);
      y += lineHeight;
    });

    return doc;
  };

  const handleCopy = async () => {
    if (!baseText.trim()) {
      toast.error("La lettre est vide. Veuillez revenir au générateur.");
      return;
    }

    try {
      await navigator.clipboard.writeText(baseText);
      setCopied(true);
      toast.success("Lettre copiée dans le presse-papiers");
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error("Impossible de copier");
    }
  };

  const handlePrint = () => {
    if (!baseText.trim()) {
      toast.error("La lettre est vide. Veuillez revenir au générateur.");
      return;
    }

    window.print();
  };

  const handlePDF = () => {
    if (!baseText.trim()) {
      toast.error("La lettre est vide. Veuillez revenir au générateur.");
      return;
    }

    const doc = buildPDFDoc();
    const fileName = `clameo-${payload.caseId}-${new Date()
      .toISOString()
      .slice(0, 10)}.pdf`;
    doc.save(fileName);
    toast.success("PDF téléchargé");
  };

  const handleZipDownload = async () => {
    if (!baseText.trim()) {
      toast.error("La lettre est vide. Veuillez revenir au générateur.");
      return;
    }

    setZipping(true);

    try {
      const zip = new JSZip();
      const dateStr = new Date().toISOString().slice(0, 10);

      const doc = buildPDFDoc();
      const pdfBytes = doc.output("arraybuffer");
      zip.file(`clameo-lettre-${payload.caseId}-${dateStr}.pdf`, pdfBytes);

      const fetchErrors = [];

      await Promise.all(
        uploadedAttachments.map(async (att, idx) => {
          const url = att.file_url || att.url;

          if (!url) {
            fetchErrors.push(att.file_name || `Fichier ${idx + 1}`);
            return;
          }

          try {
            const res = await fetch(url);
            if (!res.ok) throw new Error(`HTTP ${res.status}`);

            const buf = await res.arrayBuffer();
            const name = att.file_name || `piece-jointe-${idx + 1}`;
            zip.file(`pieces-jointes/${name}`, buf);
          } catch (err) {
            console.error("Attachment fetch error:", err);
            fetchErrors.push(att.file_name || `Fichier ${idx + 1}`);
          }
        })
      );

      if (fetchErrors.length > 0) {
        toast.warning(
          `${fetchErrors.length} pièce(s) jointe(s) n'ont pas pu être incluse(s) dans le dossier. Téléchargez-les séparément.`
        );
      }

      const zipBlob = await zip.generateAsync({ type: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(zipBlob);
      a.download = `clameo-dossier-${payload.caseId}-${dateStr}.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(a.href);
      toast.success("Dossier complet téléchargé");
    } catch (err) {
      console.error("ZIP error:", err);
      toast.error("Erreur lors de la création du dossier. Réessayez.");
    } finally {
      setZipping(false);
    }
  };

  const startEdit = () => {
    setEditedText(baseText);
    setEditing(true);
  };

  const stopEdit = () => setEditing(false);

  const resetEdit = () => {
    setEditedText(null);
    setEditing(false);
    toast.success("Texte réinitialisé");
  };

  if (!payload || !c) {
    return (
      <div className="min-h-screen bg-background text-foreground">
        <Toaster position="top-center" />
        <Header />
        <section className="max-w-xl mx-auto px-6 py-32 text-center">
          <h1 className="mb-4 text-3xl font-black tracking-[-0.04em] sm:text-4xl">
            Votre lettre n&apos;est plus disponible
          </h1>
          <p className="mb-8 text-foreground/70">
            Retournez au générateur pour créer une nouvelle lettre.
          </p>
          <Link
            to="/builder"
            className="btn-coral inline-flex items-center gap-2 rounded-[14px] px-6 py-3 text-sm font-semibold"
          >
            <ArrowLeft size={16} /> Créer une lettre
          </Link>
        </section>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Toaster position="top-center" />
      <Header />

      <section className="max-w-[1100px] mx-auto px-6 lg:px-12 py-12 lg:py-16">
        <div className="no-print mb-8 flex items-center justify-between">
          <Link
            to="/builder"
            className="inline-flex items-center gap-2 text-sm text-foreground/60 hover:text-foreground"
            data-testid="result-back"
          >
            <ArrowLeft size={16} /> Modifier ma lettre
          </Link>

          {c && (
            <p className="eyebrow hidden text-foreground/60 sm:block">
              {c.title}
            </p>
          )}
        </div>

        <div className="no-print">
          <p className="eyebrow text-foreground/60">Votre dossier</p>
          <h1
            className="mt-3 text-4xl font-black leading-[1.05] tracking-[-0.04em] sm:text-5xl lg:text-6xl"
            data-testid="result-title"
          >
            Votre dossier est <em className="italic text-[#e8502a]">prêt</em>
          </h1>
          <p className="mt-4 max-w-xl text-foreground/70">
            {hasAttachments
              ? "Relisez votre lettre, vérifiez les pièces jointes, puis téléchargez votre dossier complet."
              : "Relisez, ajustez le texte si nécessaire, puis téléchargez votre lettre."}
          </p>
        </div>

        <div className="mt-10 grid grid-cols-1 gap-10 lg:grid-cols-12">
          <div className="lg:col-span-8">
            <div className="no-print mb-3 flex items-center gap-2">
              {!editing ? (
                <button
                  type="button"
                  onClick={startEdit}
                  className="inline-flex items-center gap-2 rounded-[14px] border border-border px-4 py-2 text-xs font-medium transition hover:border-foreground"
                  data-testid="action-edit"
                >
                  <Pencil size={14} /> Modifier le texte
                </button>
              ) : (
                <button
                  type="button"
                  onClick={stopEdit}
                  className="inline-flex items-center gap-2 rounded-[14px] border border-border px-4 py-2 text-xs font-medium transition hover:border-foreground"
                >
                  <Eye size={14} /> Aperçu
                </button>
              )}

              {isEdited && (
                <button
                  type="button"
                  onClick={resetEdit}
                  className="inline-flex items-center gap-2 rounded-[14px] border border-border px-4 py-2 text-xs font-medium transition hover:border-foreground"
                  data-testid="action-reset"
                >
                  <RotateCcw size={14} /> Réinitialiser
                </button>
              )}

              {isEdited && (
                <span
                  className="inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-semibold"
                  style={{ borderColor: "#e5e7eb", color: "#e8502a" }}
                  data-testid="edited-badge"
                >
                  Modifié
                </span>
              )}
            </div>

            <div
              className="printable-letter relative overflow-hidden rounded-sm bg-white letter-shadow"
              data-testid="result-letter"
            >
              <div className="absolute left-0 right-0 top-0 h-[3px] bg-coral" />

              {editing ? (
                <textarea
                  value={baseText}
                  onChange={(e) => setEditedText(e.target.value)}
                  className="w-full resize-none border-0 bg-white p-6 font-mono-letter text-[13px] leading-[1.7] text-foreground outline-none focus:ring-0 sm:p-9 sm:text-[14px]"
                  style={{ minHeight: "70vh" }}
                  data-testid="result-editor"
                  spellCheck={true}
                />
              ) : (
                <pre className="whitespace-pre-wrap p-6 font-mono-letter text-[13px] leading-[1.7] text-foreground sm:p-9 sm:text-[14px]">
                  {baseText}
                </pre>
              )}
            </div>

            {hasAttachments && (
              <div className="no-print mt-8 rounded-lg border border-border bg-card p-6">
                <p className="mb-4 flex items-center gap-2 text-lg font-semibold">
                  <FileText size={20} className="text-coral" />
                  Pièces jointes
                </p>

                <div className="space-y-3">
                  {uploadedAttachments.map((attachment, index) => (
                    <div
                      key={attachment.id || index}
                      className="flex items-center justify-between rounded-[14px] border border-border bg-white p-3"
                    >
                      <div className="flex items-center gap-3">
                        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-coral/10">
                          <FileText size={20} className="text-coral" />
                        </div>

                        <div>
                          <p className="text-sm font-medium">
                            {attachment.file_name || `Fichier ${index + 1}`}
                          </p>
                          <p className="text-xs text-foreground/60">
                            {attachment.file_size
                              ? `${(attachment.file_size / 1024 / 1024).toFixed(2)} Mo`
                              : "PDF"}
                          </p>
                        </div>
                      </div>

                      {(attachment.file_url || attachment.url) && (
                        <a
                          href={attachment.file_url || attachment.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm font-medium text-coral hover:underline"
                          download
                        >
                          Télécharger
                        </a>
                      )}
                    </div>
                  ))}
                </div>

                <p className="mt-4 text-xs text-foreground/60">
                  Ces fichiers servent à préparer votre dossier. Ils ne sont pas
                  envoyés automatiquement au destinataire.
                </p>
              </div>
            )}

            <ResultInfoCards requiresLRAR={requiresLRAR} />
          </div>

          <aside className="no-print mt-6 space-y-3 lg:col-span-4 lg:mt-0">
            <div className="rounded-lg border border-border bg-card p-6">
              <p className="text-xl font-semibold">Actions</p>
              <p className="mt-1 text-sm text-foreground/60">
                {hasAttachments
                  ? "Téléchargez votre dossier, copiez le texte ou imprimez la lettre."
                  : "Téléchargez, copiez ou imprimez votre lettre."}
              </p>

              <div className="mt-5 space-y-2.5">
                {hasAttachments ? (
                  <>
                    <button
                      type="button"
                      onClick={handleZipDownload}
                      disabled={zipping}
                      className="btn-coral inline-flex w-full items-center justify-center gap-2 rounded-[14px] px-5 py-3.5 text-sm font-semibold disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {zipping ? (
                        <>
                          <svg
                            className="h-4 w-4 animate-spin"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            />
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            />
                          </svg>
                          Préparation…
                        </>
                      ) : (
                        <>
                          <Package size={16} /> Télécharger le dossier complet
                        </>
                      )}
                    </button>

                    <button
                      type="button"
                      onClick={handlePDF}
                      className="inline-flex w-full items-center justify-center gap-2 rounded-[14px] border border-border px-5 py-3.5 text-sm font-medium transition hover:border-foreground"
                    >
                      <Download size={16} /> Télécharger la lettre en PDF
                    </button>
                  </>
                ) : (
                  <button
                    type="button"
                    onClick={handlePDF}
                    className="btn-coral inline-flex w-full items-center justify-center gap-2 rounded-[14px] px-5 py-3.5 text-sm font-semibold"
                  >
                    <Download size={16} /> Télécharger en PDF
                  </button>
                )}

                <button
                  type="button"
                  onClick={handleCopy}
                  className="inline-flex w-full items-center justify-center gap-2 rounded-[14px] border border-border px-5 py-3.5 text-sm font-medium transition hover:border-foreground"
                >
                  {copied ? (
                    <Check size={16} className="text-success" />
                  ) : (
                    <Copy size={16} />
                  )}
                  {copied ? "Copié !" : "Copier le texte"}
                </button>

                <button
                  type="button"
                  onClick={handlePrint}
                  className="inline-flex w-full items-center justify-center gap-2 rounded-[14px] border border-border px-5 py-3.5 text-sm font-medium transition hover:border-foreground"
                >
                  <Printer size={16} /> Imprimer
                </button>
              </div>
            </div>

            <MerciFacteurCard caseId={payload.caseId} />

            {showIncogniPartner && <IncogniCard caseId={payload.caseId} />}

            {showLebaraPartner && <LebaraCard caseId={payload.caseId} />}

            {showTopCashbackPartner && (
              <TopCashbackCard caseId={payload.caseId} />
            )}
          </aside>
        </div>
      </section>

      <Footer />
    </div>
  );
}
